import type { EntryType } from '@fcbh/atlas-schema';

export type BlurbCacheEntry = {
  type: EntryType;
  slug: string;
  text: string;
  href: string;
  lang: string;
  fetchedAt: number;
  stale?: true;
};

const STORE = new Map<string, BlurbCacheEntry>();
const PENDING = new Map<string, Promise<BlurbCacheEntry | undefined>>();

const ATLAS_HOST =
  (typeof window !== 'undefined' &&
    (window as unknown as { __ATLAS_HOST__?: string }).__ATLAS_HOST__) ||
  'https://atlas.battlefield.app';

function key(type: EntryType, slug: string, lang: string): string {
  return `${type}:${slug}:${lang}`;
}

export function getCachedBlurb(
  type: EntryType,
  slug: string,
  lang: string,
): BlurbCacheEntry | undefined {
  return STORE.get(key(type, slug, lang));
}

export async function fetchBlurb(
  type: EntryType,
  slug: string,
  lang = 'en',
): Promise<BlurbCacheEntry | undefined> {
  const k = key(type, slug, lang);
  const cached = STORE.get(k);
  if (cached) return cached;
  const pending = PENDING.get(k);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const url = `${ATLAS_HOST}/api/atlas/entries/${type}/${slug}/blurb?lang=${encodeURIComponent(lang)}`;
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) return undefined;
      const data = (await res.json()) as {
        type: EntryType;
        slug: string;
        blurb: { text: string; href: string; lang: string };
      };
      const entry: BlurbCacheEntry = {
        type: data.type,
        slug: data.slug,
        text: data.blurb.text,
        href: data.blurb.href,
        lang: data.blurb.lang,
        fetchedAt: Date.now(),
      };
      STORE.set(k, entry);
      return entry;
    } catch {
      return undefined;
    } finally {
      PENDING.delete(k);
    }
  })();
  PENDING.set(k, promise);
  return promise;
}

export type AtlasDeepLinkOpts = {
  ref?: string;
  from?: string;
  returnTo?: string;
  /** Reader color-profile slug — Atlas mirrors light/dark accordingly */
  palette?: string;
};

export function atlasDeepLink(
  type: EntryType,
  slug: string,
  lang = 'en',
  opts: AtlasDeepLinkOpts = {},
): string {
  const base = `${ATLAS_HOST}/${lang}/${type}/${slug}`;
  const params = new URLSearchParams();
  if (opts.ref) params.set('ref', opts.ref);
  if (opts.from) params.set('from', opts.from);
  if (opts.returnTo) params.set('returnTo', opts.returnTo);
  if (opts.palette) params.set('palette', opts.palette);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function currentReaderReturnTo(verseRef?: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const origin = window.location.origin;
  const path = window.location.pathname;
  if (verseRef) {
    const u = new URL(origin + path);
    u.searchParams.set('ref', verseRef);
    return u.toString();
  }
  return origin + path;
}
